import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import { OrderType } from "../types";
import { useTradeUrlState } from "../tradeUrlState";

type ScaleForm = {
  start?: bigint;
  end?: bigint;
  totalOrders?: bigint;
  skewSize?: bigint;
};

type TradeContextValue = {
  price: bigint;
  setPrice: (v: bigint) => void;
  isPriceSet: boolean;
  scaleForm: ScaleForm;
  setScaleForm: (v: ScaleForm) => void;
  premium: bigint;
  setPremium: (v: bigint) => void;
  amount: bigint;
  setAmount: (v: bigint) => void;
  address?: string;
  base?: string;
  quote?: string;
  cclHigh?: bigint;
  cclLow?: bigint;
  orderType: OrderType;
  setOrderType: (v: OrderType) => void;
};

const defaultContext: TradeContextValue = {
  price: 0n,
  setPrice: () => {},
  isPriceSet: false,
  scaleForm: {},
  setScaleForm: () => {},
  premium: 0n,
  setPremium: () => {},
  amount: 0n,
  setAmount: () => {},
  address: undefined,
  base: undefined,
  quote: undefined,
  orderType: OrderType.Limit,
  setOrderType: () => {},
};

const context = createContext(defaultContext);

export const TradeContext: FC<
  PropsWithChildren<{
    address?: string;
    base?: string;
    quote?: string;
  }>
> = ({ children, address, base, quote }) => {
  // This provider outlives a pair switch, so a price only counts for the pair
  // it was entered on
  const [priced, setPriced] = useState<{ address?: string; value: bigint }>();
  const isPriceSet = !!priced && priced.address === address;
  const price = isPriceSet ? priced.value : 0n;
  const setPrice = useCallback(
    (value: bigint) => setPriced({ address, value }),
    [address]
  );
  const [pairScaleForm, setPairScaleForm] = useState<
    ScaleForm & { address?: string }
  >();
  const scaleForm: ScaleForm =
    pairScaleForm && pairScaleForm.address === address ? pairScaleForm : {};
  const setScaleForm = useCallback(
    (value: ScaleForm) =>
      setPairScaleForm((prev) => ({
        ...(prev?.address === address ? prev : {}),
        ...value,
        address,
      })),
    [address]
  );
  const [premium, setPremium] = useState(0n);
  const [amount, setAmount] = useState(0n);
  const [tradeUrlState, setTradeUrlState] = useTradeUrlState();
  const orderType =
    tradeUrlState.type === "manual" ? tradeUrlState.order : OrderType.Market;
  const setOrderType = useCallback(
    (order: OrderType) => setTradeUrlState({ type: "manual", order }),
    [setTradeUrlState]
  );

  return (
    <context.Provider
      value={{
        price,
        setPrice,
        isPriceSet,
        scaleForm,
        setScaleForm,
        premium,
        setPremium,
        amount,
        setAmount,
        address,
        base,
        quote,
        orderType,
        setOrderType,
      }}>
      {children}
    </context.Provider>
  );
};

export const usePrice = (): [bigint, (v: bigint) => void, boolean] => {
  const { price, setPrice, isPriceSet } = useContext(context);
  return [price, setPrice, isPriceSet];
};

export const useScaleForm = (): [ScaleForm, (v: ScaleForm) => void] => {
  const { scaleForm, setScaleForm } = useContext(context);
  return [scaleForm, setScaleForm];
};

export const usePremium = (): [bigint, (v: bigint) => void] => {
  const { premium, setPremium } = useContext(context);
  return [premium, setPremium];
};

export const useAmount = (): [bigint, (v: bigint) => void] => {
  const { amount, setAmount } = useContext(context);
  return [amount, setAmount];
};

export const useOrderType = (): [OrderType, (v: OrderType) => void] => {
  const { orderType, setOrderType } = useContext(context);
  return [orderType, setOrderType];
};

export const useTradeContext = (): {
  base?: string;
  quote?: string;
  address?: string;
} => {
  const { base, quote, address } = useContext(context);
  return { base, quote, address };
};
